/**
 * Admin Documents tab for the report page, in two sections:
 *
 * "Assignment documents" — every file the Network Partners submitted
 * (docx, pdf, images, anything) with an in-app preview for each, and
 * marks which docx is currently anchored as "the report" (the one
 * clients see). Admins can re-anchor a different docx; the backend
 * refuses while an approval is standing (revoke first) and versions the
 * content so the viewer refreshes rather than silently swapping bytes.
 *
 * "Project documents" — the parent request's attached source documents
 * (what gets dispatched to the Backoffice project), moved here from the
 * request-context bar's popover.
 */

import { useState } from 'react';

import {
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import type { DispatchedPartner } from '@/components/ReportPartners';
import { useBackofficeFiles } from '@/hooks/useBackofficeFiles';
import { useReportRequestAttachments } from '@/hooks/useReportRequestAttachments';
import { vnextApiClient } from '@/services/vnext-api';
import { isManagedAssignment, type BackofficeAssignment } from '@/types/np-assignments';
import type { BackofficeFile } from '@/types/report';
import { FILE_TYPE_COLORS, FilePreview } from './FilePreview';

/** "20 Jul 2026, 14:32" — enough to tell submission rounds apart. */
function formatReceivedAt(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface ReportDocumentsPanelProps {
  reportId: string;
  /** Parent request — non-null adds the "Project documents" section (the
   * request's attached source documents). */
  reportRequestId?: string | null;
  /** False for reports without Backoffice involvement — hides the
   * assignment-documents section. */
  assignmentDocsEnabled?: boolean;
  /** Whether to fetch the report's files at all. Defaults to
   * `assignmentDocsEnabled`; a report without Backoffice involvement still
   * needs them for the full lists its sections cap. */
  filesEnabled?: boolean;
  /** Fired after a successful re-anchor — the page reloads the viewer. */
  onAnchored?: () => void;
  /** Dispatched partners (from the page's assignments fetch) — enables
   * the QA-loop "Send to NP" action. */
  partners?: DispatchedPartner[];
  /** Fired after a successful send — the page refetches assignments so
   * the rework timestamp surfaces. */
  onSentToNp?: () => void | Promise<void>;
}

/** Whether rework can be sent to this assignment's partner: it reached
 * Backoffice, and is not Armadillo's automatic assignment. */
function isSendToNpTarget(assignment: BackofficeAssignment): boolean {
  return assignment.status === 'sent_to_backoffice' && !isManagedAssignment(assignment);
}

export function ReportDocumentsPanel({
  reportId,
  reportRequestId = null,
  assignmentDocsEnabled = true,
  filesEnabled = assignmentDocsEnabled,
  onAnchored,
  partners = [],
  onSentToNp,
}: Readonly<ReportDocumentsPanelProps>) {
  const [previewFile, setPreviewFile] = useState<BackofficeFile | null>(null);
  const [anchorCandidate, setAnchorCandidate] = useState<BackofficeFile | null>(null);
  const [sendOpen, setSendOpen] = useState(false);

  const {
    files: allFiles,
    isLoading,
    error,
    isSelecting,
    selectFile,
    refetch,
  } = useBackofficeFiles(filesEnabled ? reportId : null);
  // Overflow files are report assets, not partner submissions: they have no
  // share switch or anchor action, so they get their own section.
  const files = allFiles.filter((f) => f.kind !== 'overflow');
  const overflowFiles = allFiles.filter((f) => f.kind === 'overflow');
  const { attachments: projectDocs } = useReportRequestAttachments(reportRequestId ?? undefined);

  // Absent before the first selection (awaiting_file_selection) — the
  // confirm copy switches from "replace" to "finalise" accordingly.
  const hasCurrentReport = files.some((f) => f.kind === 'current_report');

  const handleShareToggle = async (file: BackofficeFile, share: boolean) => {
    try {
      await vnextApiClient.setBackofficeFileVisibility(reportId, file.raw_data_key, share);
      await refetch();
    } catch (err) {
      notifications.show({
        title: share ? 'Could not share the file' : 'Could not unshare the file',
        message: err instanceof Error ? err.message : 'An error occurred.',
        color: 'red',
      });
    }
  };

  const confirmAnchor = async () => {
    if (!anchorCandidate) {
      return;
    }
    try {
      await selectFile(anchorCandidate.raw_data_key);
      notifications.show({
        title: 'Report file updated',
        message: `${anchorCandidate.filename} is now the report clients see.`,
        color: 'green',
      });
      setAnchorCandidate(null);
      await refetch();
      onAnchored?.();
    } catch (err) {
      // The 409 carries the useful text ("revoke approval first").
      notifications.show({
        title: 'Could not update the report file',
        message: err instanceof Error ? err.message : 'An error occurred.',
        color: 'red',
      });
    }
  };

  // Rework goes to a partner's Backoffice request. Armadillo's automatic
  // assignment supplies documents, so there is nothing to send back, and an
  // Armadillo assignment listed before it was dispatched has no request.
  const sendableAssignments = partners.flatMap((p) =>
    p.assignments.filter(isSendToNpTarget).map((a) => ({
      value: a.id,
      label: `${p.networkPartner.name ?? p.networkPartner.email ?? p.npId}${
        a.reworkSentAt ? ` — rework sent ${new Date(a.reworkSentAt).toLocaleDateString()}` : ''
      }`,
    }))
  );

  const assignmentSection = !assignmentDocsEnabled ? null : (
    <>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={2}>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed">
            Assignment documents
          </Text>
          <Text size="xs" c="dimmed">
            The current report plus everything the Network Partners submitted. Anchoring a submitted
            docx makes it the report clients see. Ferretly screening reports are listed here for
            admins only.
          </Text>
        </Stack>
        {hasCurrentReport && sendableAssignments.length > 0 && (
          <Button
            size="compact-xs"
            variant="light"
            onClick={() => setSendOpen(true)}
            style={{ flexShrink: 0 }}
            data-testid="send-to-np"
          >
            Send to NP
          </Button>
        )}
      </Group>
      {isLoading && files.length === 0 && (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      )}
      {error && (
        <Text c="red" size="sm">
          {error.message}
        </Text>
      )}
      {!isLoading && !error && files.length === 0 && (
        <Text c="dimmed" size="sm">
          No Network Partner documents on this report yet.
        </Text>
      )}
      <Stack gap={6}>
        {files.map((file) => (
          <Stack
            key={file.raw_data_key}
            gap={4}
            p="xs"
            style={{
              border: '1px solid var(--mantine-color-default-border)',
              borderRadius: 'var(--mantine-radius-sm)',
            }}
            data-testid={`report-document-${file.filename}`}
          >
            <Group gap="xs" wrap="nowrap" justify="space-between">
              <Text size="sm" fw={500} truncate style={{ minWidth: 0 }}>
                {file.filename}
              </Text>
              <Group gap={6} wrap="nowrap">
                <Badge color={FILE_TYPE_COLORS[file.file_type]} variant="light" size="sm">
                  {file.file_type.toUpperCase()}
                </Badge>
                {file.is_resubmission && (
                  <Badge
                    color="orange"
                    variant="light"
                    size="sm"
                    data-testid={`resubmission-badge-${file.filename}`}
                  >
                    Resubmission
                  </Badge>
                )}
                {file.kind === 'ferretly_report' && (
                  <Badge color="gray" variant="outline" size="sm" data-testid="admin-only-badge">
                    Admin only
                  </Badge>
                )}
                {file.kind === 'current_report' ? (
                  <Badge color="teal" size="sm" data-testid="current-report-badge">
                    Current report
                  </Badge>
                ) : (
                  file.is_current && (
                    <Badge
                      color="teal"
                      variant="light"
                      size="sm"
                      data-testid="anchored-source-badge"
                    >
                      Anchored source
                    </Badge>
                  )
                )}
              </Group>
            </Group>
            {file.created_at && (
              <Text size="xs" c="dimmed" data-testid={`received-at-${file.filename}`}>
                Received {formatReceivedAt(file.created_at)}
              </Text>
            )}
            <Group gap="xs" justify="space-between">
              <Group gap="xs">
                <Button size="compact-xs" variant="default" onClick={() => setPreviewFile(file)}>
                  View
                </Button>
                {file.kind === 'submission' && file.file_type === 'docx' && !file.is_current && (
                  <Button
                    size="compact-xs"
                    variant="light"
                    onClick={() => setAnchorCandidate(file)}
                    data-testid={`anchor-report-${file.filename}`}
                  >
                    Make this the report
                  </Button>
                )}
              </Group>
              {file.kind === 'submission' && (
                <Switch
                  size="xs"
                  label="Share with client"
                  labelPosition="left"
                  checked={file.client_visible}
                  onChange={(e) => void handleShareToggle(file, e.currentTarget.checked)}
                  data-testid={`share-file-${file.filename}`}
                />
              )}
            </Group>
          </Stack>
        ))}
      </Stack>
    </>
  );

  const overflowSection =
    overflowFiles.length > 0 ? (
      <Stack gap={6} data-testid="full-lists-section">
        <Stack gap={2}>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed">
            Full lists
          </Text>
          <Text size="xs" c="dimmed">
            The complete lists behind sections the report caps. They are shared with the client
            whenever the report is visible to them.
          </Text>
        </Stack>
        {overflowFiles.map((file) => (
          <Stack
            key={file.raw_data_key}
            gap={4}
            p="xs"
            style={{
              border: '1px solid var(--mantine-color-default-border)',
              borderRadius: 'var(--mantine-radius-sm)',
            }}
            data-testid={`report-document-${file.filename}`}
          >
            <Group gap="xs" wrap="nowrap" justify="space-between">
              <Text size="sm" fw={500} truncate style={{ minWidth: 0 }}>
                {file.filename}
              </Text>
              <Group gap={6} wrap="nowrap">
                <Badge color="indigo" variant="light" size="sm">
                  Full list
                </Badge>
                <Badge color={FILE_TYPE_COLORS[file.file_type]} variant="light" size="sm">
                  {file.file_type.toUpperCase()}
                </Badge>
              </Group>
            </Group>
            {file.created_at && (
              <Text size="xs" c="dimmed">
                Generated {formatReceivedAt(file.created_at)}
              </Text>
            )}
            <Group gap="xs">
              <Button size="compact-xs" variant="default" onClick={() => setPreviewFile(file)}>
                View
              </Button>
              <Anchor href={file.presigned_url} size="xs" download={file.filename}>
                Download
              </Anchor>
            </Group>
          </Stack>
        ))}
      </Stack>
    ) : null;

  const projectSection =
    reportRequestId && projectDocs.length > 0 ? (
      <Stack gap={6} data-testid="project-documents-section">
        <Stack gap={2}>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed">
            Project documents
          </Text>
          <Text size="xs" c="dimmed">
            Source documents attached to the request — dispatched with the project.
          </Text>
        </Stack>
        {projectDocs.map((doc) => (
          <Group
            key={doc.id}
            gap="xs"
            wrap="nowrap"
            p="xs"
            style={{
              border: '1px solid var(--mantine-color-default-border)',
              borderRadius: 'var(--mantine-radius-sm)',
            }}
            data-testid={`project-document-${doc.filename}`}
          >
            {doc.downloadUrl ? (
              <Anchor
                href={doc.downloadUrl}
                target="_blank"
                rel="noreferrer"
                size="sm"
                fw={500}
                truncate
                style={{ minWidth: 0 }}
              >
                {doc.filename ?? 'file'}
              </Anchor>
            ) : (
              <Text size="sm" fw={500} truncate style={{ minWidth: 0 }}>
                {doc.filename ?? 'file'}
              </Text>
            )}
            {doc.classification && (
              <Badge
                size="xs"
                variant={doc.classification === 'client' ? 'light' : 'outline'}
                color={doc.classification === 'client' ? 'teal' : 'gray'}
                ml="auto"
                style={{ flexShrink: 0 }}
              >
                {doc.classification === 'client' ? 'Client' : 'Internal'}
              </Badge>
            )}
          </Group>
        ))}
      </Stack>
    ) : null;

  return (
    <Stack gap="xs" style={{ minHeight: 0, flex: 1 }}>
      <ScrollArea style={{ flex: 1, minHeight: 0 }}>
        <Stack gap="md">
          {assignmentSection}
          {overflowSection}
          {projectSection}
          {!assignmentSection && !overflowSection && !projectSection && (
            <Text c="dimmed" size="sm">
              No documents on this report yet.
            </Text>
          )}
        </Stack>
      </ScrollArea>

      <Modal
        opened={previewFile !== null}
        onClose={() => setPreviewFile(null)}
        title={previewFile?.filename}
        size="90%"
      >
        <Box style={{ height: '75vh' }}>
          {previewFile && <FilePreview file={previewFile} reportId={reportId} />}
        </Box>
      </Modal>

      <Modal
        opened={anchorCandidate !== null}
        onClose={() => setAnchorCandidate(null)}
        title="Replace the report file?"
      >
        <Stack gap="sm">
          <Text size="sm">
            {hasCurrentReport ? (
              <>
                <strong>{anchorCandidate?.filename}</strong> will become the report clients see, as
                a new content version.
              </>
            ) : (
              <>
                <strong>{anchorCandidate?.filename}</strong> becomes the report clients see, and the
                report is finalised.
              </>
            )}
          </Text>
          {hasCurrentReport && (
            <Text size="sm" c="dimmed">
              Feedback anchored to the current file will no longer line up, and an approved report
              must have its approval revoked first.
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAnchorCandidate(null)}>
              Cancel
            </Button>
            <Button
              loading={isSelecting}
              onClick={() => void confirmAnchor()}
              data-testid="confirm-anchor"
            >
              Make it the report
            </Button>
          </Group>
        </Stack>
      </Modal>

      <SendToNpModal
        opened={sendOpen}
        onClose={() => setSendOpen(false)}
        reportId={reportId}
        assignments={sendableAssignments}
        onSent={onSentToNp}
      />
    </Stack>
  );
}

/** QA loop: pushes the CURRENT report docx to the partner's Backoffice
 * assignment (unlocking resubmission on their Submit Assignment page),
 * optionally with a message on their NP thread. Their next submit comes
 * back as an "NP resubmitted" inbox item. */
function SendToNpModal({
  opened,
  onClose,
  reportId,
  assignments,
  onSent,
}: Readonly<{
  opened: boolean;
  onClose: () => void;
  reportId: string;
  assignments: { value: string; label: string }[];
  onSent?: () => void | Promise<void>;
}>) {
  const [assignmentId, setAssignmentId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const selected = assignmentId ?? assignments[0]?.value ?? null;

  const send = async () => {
    if (!selected) {
      return;
    }
    setSending(true);
    try {
      const result = await vnextApiClient.sendReportToNp(reportId, {
        assignment_id: selected,
        message: message.trim() || null,
      });
      notifications.show({
        title: 'Sent to the partner',
        message:
          result.message_sent === false
            ? 'Document delivered, but the message could not be sent — follow up on the NP thread.'
            : 'The partner can now rework and resubmit their assignment.',
        color: result.message_sent === false ? 'yellow' : 'green',
      });
      setMessage('');
      onClose();
      await onSent?.();
    } catch (err) {
      notifications.show({
        title: 'Could not send to the partner',
        message: err instanceof Error ? err.message : 'An error occurred.',
        color: 'red',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Send to Network Partner">
      <Stack gap="sm">
        <Text size="sm">
          Sends the <strong>current report docx</strong> to the partner's Backoffice assignment and
          unlocks resubmission. Their next submit shows up here as an actionable item.
        </Text>
        {assignments.length > 1 && (
          <Select
            label="Partner assignment"
            data={assignments}
            value={selected}
            onChange={setAssignmentId}
            allowDeselect={false}
            data-testid="send-to-np-assignment"
          />
        )}
        <Textarea
          label="Message to the partner"
          description="Optional — sent on their conversation thread (they'll get it by email)."
          placeholder="e.g. Please address the tracked comments in sections 2 and 4."
          value={message}
          onChange={(e) => setMessage(e.currentTarget.value)}
          minRows={3}
          autosize
          data-testid="send-to-np-message"
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            loading={sending}
            disabled={!selected}
            onClick={() => void send()}
            data-testid="confirm-send-to-np"
          >
            Send
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
